import classNames from 'classnames';
import { Link } from 'gatsby';
import React, { startTransition, use, useEffect, useState } from 'react';

import ArrowRightThick from 'src/components/icons/arrow-right-thick';
import TimeCircle from 'src/components/icons/time-circle';
import {
  backgroundColorForRecentClubRibbon,
  createAnimatedStyles,
  isPromoEnabledOnPage,
} from 'src/components/layout/redesign/header-shared/promo-helper';
import type { PromoRibbonProps } from 'src/components/layout/redesign/header-shared/promo-ribbon';
import { getClubInformation } from 'src/content/club-look-ups-by-id';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import { useRibbonEffects } from 'src/custom-hooks/use-ribbon-effect';
import { getBuildDomainForBuildType } from 'src/enums/build/build-domain';
import { getClubDisplayValues } from 'src/enums/build/build-type';
import { amplitudeBrandName, amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import { clubNameDependingOnBuildTypeAndLanguage } from 'src/helpers/club-helper';
import { getLocalStorageItem, removeLocalStorageItem } from 'src/helpers/local-storage-helper';
import { getAbsoluteClubPagePath, getClubPagePath } from 'src/helpers/navigation-helper';
import { RecentlyViewedClubStorageKey } from 'src/helpers/recently-viewed-club-storage-keys';
import { TranslatedStaticText } from 'src/translators/static-text-translator';

import * as styles from './recently-viewed-club-ribbon.module.scss';

type RecentlyViewedClubRibbonProps = Omit<PromoRibbonProps, 'promoRibbon'> & {
  isMobile?: boolean;
  enabled: boolean;
  setShouldShowClubRibbon: (value: boolean) => void;
};

const RecentlyViewedClubRibbon = ({
  isMobileNavOpen,
  onPointerOver,
  isMobile,
  enabled,
  setShouldShowClubRibbon,
}: RecentlyViewedClubRibbonProps) => {
  const { headerContent } = use(GlobalCmsContent);
  const { styling, language, buildType, basePath, pageLevel, isMeridian } = use(SiteContext);

  const ribbon = headerContent?.headerRecentlyViewedClubRibbon;
  const [clubId, setClubId] = useState<string | null>();
  useEffect(() => {
    startTransition(() => {
      setClubId(getLocalStorageItem(RecentlyViewedClubStorageKey.clubId));
    });
  }, []);

  const {
    componentHeight,
    shouldHide,
    componentRef,
    textAndChevronContainerRef,
    textRef,
    chevronRef,
  } = useRibbonEffects(0, !!clubId);

  if (!clubId) {
    return null;
  }

  const { clubName, buildType: clubBuildType } = getClubInformation(clubId);
  const clubInformation = getClubInformation(clubId);

  if (!isPromoEnabledOnPage(ribbon, basePath, language, buildType) || !clubName) {
    return null;
  }

  const animatedStyles = createAnimatedStyles(
    componentHeight,
    shouldHide || !enabled,
    isMobileNavOpen
  );

  const handleClick = () => {
    void amplitudeTrackEvent(`Click to Recently viewed Club Ribbon`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      component: 'Recently viewed Club Ribbon',
      ctaName: 'Recently viewed Club Ribbon',
    });
  };

  const onClickRemove = () => {
    removeLocalStorageItem(RecentlyViewedClubStorageKey.clubId);
    setShouldShowClubRibbon(false);
  };

  const clubNamePrefix = getClubDisplayValues(clubBuildType).clubNamePrefix;
  const fullClubName =
    `${clubNamePrefix} ${clubNameDependingOnBuildTypeAndLanguage(clubName, buildType, language)}`.split(
      ' '
    );
  const lastPartClubName = fullClubName.pop();
  const beforeLastPartClubName = fullClubName.join(' ');

  const linkChildren = (
    <>
      <span className={styles.clubNameStyle}>{`${beforeLastPartClubName} `}</span>
      <span className={styles.textWithArrow}>
        {lastPartClubName}
        <ArrowRightThick
          height="0.8125rem"
          fill={isMeridian ? styling.colors.meridianBlue : styling.colors.camelot}
          size={17}
          svgStyle={{ marginLeft: '0.2rem' }}
        />
      </span>
    </>
  );

  const isSameDomain =
    getBuildDomainForBuildType(clubInformation.buildType) === getBuildDomainForBuildType(buildType);

  return (
    <div
      css={[
        animatedStyles.openCloseWrapper,
        {
          backgroundColor: backgroundColorForRecentClubRibbon(styling, ribbon?.color),
        },
      ]}
      onPointerOver={onPointerOver}
    >
      <div
        className={styles.wrapper}
        ref={componentRef}
        data-testid={`recently-viewed-club-ribbon-${componentHeight}`}
      >
        <div
          className={classNames(styles.container, isMobile && styles.mobile)}
          ref={textAndChevronContainerRef}
        >
          <div className={styles.textWrapper} ref={textRef}>
            <span className={styles.spacedRight}>{ribbon?.text}</span>
            {isSameDomain ? (
              <Link
                to={getClubPagePath('', clubId, language)}
                className={styles.link}
                onClick={handleClick}
              >
                {linkChildren}
              </Link>
            ) : (
              <a
                href={getAbsoluteClubPagePath('', clubId, language)}
                className={styles.link}
                onClick={handleClick}
              >
                {linkChildren}
              </a>
            )}
          </div>
          <div
            ref={chevronRef}
            tabIndex={0}
            className={classNames(styles.closeWrapper, isMobile && styles.mobile)}
            onClick={onClickRemove}
          >
            {!isMobile && (
              <span className={styles.spacedRight}>
                <TranslatedStaticText lookupKey="remove" />
              </span>
            )}
            <TimeCircle fill={styling.colors.capeCod} size="1rem" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentlyViewedClubRibbon;
